import React, { useState, useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { PWAProvider } from "@/context/PWAContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import MobileBottomNav from "@/components/MobileBottomNav";
import ScrollToTop from "@/components/ScrollToTop";
import CustomerPortalLayout from "@/components/CustomerPortalLayout";
import { Toaster } from "@/components/ui/sonner";

// Lazy-loaded pages for better performance
const HomePage = lazy(() => import("@/pages/HomePage"));
const GaragePage = lazy(() => import("@/pages/GaragePage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const VehiclesPage = lazy(() => import("@/pages/VehiclesPage"));
const AcademyPage = lazy(() => import("@/pages/AcademyPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const CookiePolicyPage = lazy(() => import("@/pages/CookiePolicyPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const InsightsAcademyPage = lazy(() => import("@/pages/InsightsAcademyPage"));
const GarageXpertLogin = lazy(() => import("@/pages/GarageXpertLogin"));
const GarageXpertDashboard = lazy(() => import("@/pages/GarageXpertDashboardV2"));
const AIGaragePage = lazy(() => import("@/pages/AIGaragePage"));
const CompanyPortalPage = lazy(() => import("@/pages/CompanyPortalPage"));
const ConnectHubPage = lazy(() => import("@/pages/ConnectHubPage"));
const LiveDisplayPublic = lazy(() => import("@/pages/LiveDisplayPublic"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// AppContent component to use location hook
const AppContent = ({ isPWA }) => {
  const location = useLocation();
  const isGarageXpert = location.pathname.startsWith('/garagexpert');
  const isAIGarage = location.pathname.startsWith('/ai-garage');
  const isCompanyPortal = location.pathname.startsWith('/company-portal');
  const isConnectHub = location.pathname === '/connect';
  const isLiveDisplay = location.pathname.startsWith('/live-display');
  
  // Customer Portal routes - use standalone layout without website nav/footer
  const portalRoutes = ['/garage', '/login', '/signup', '/profile'];
  const isPortalRoute = portalRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  
  // Determine if we should show website chrome (nav/footer)
  const showWebsiteChrome = !isPWA && !isGarageXpert && !isPortalRoute && !isAIGarage && !isCompanyPortal && !isConnectHub && !isLiveDisplay;
  
  return (
    <>
      <ScrollToTop />
      {/* Show Navbar only for regular website pages */}
      {showWebsiteChrome && <Navbar />}
      
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Main Website Pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/vehicles" element={<VehiclesPage />} />
        <Route path="/academy" element={<InsightsAcademyPage />} />
        <Route path="/insights" element={<InsightsAcademyPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/booking" element={<BookingPage />} />
        
        {/* Customer Portal - Standalone Layout */}
        <Route path="/garage" element={
          <CustomerPortalLayout>
            <GaragePage />
          </CustomerPortalLayout>
        } />
        <Route path="/login" element={
          <CustomerPortalLayout>
            <LoginPage />
          </CustomerPortalLayout>
        } />
        <Route path="/signup" element={
          <CustomerPortalLayout>
            <SignupPage />
          </CustomerPortalLayout>
        } />
        <Route path="/profile" element={
          <CustomerPortalLayout>
            <ProfilePage />
          </CustomerPortalLayout>
        } />
        
        {/* Admin */}
        <Route path="/admin" element={<AdminPage />} />
        
        {/* GarageXpert Admin Panel */}
        <Route path="/garagexpert/login" element={<GarageXpertLogin />} />
        <Route path="/garagexpert" element={<GarageXpertDashboard />} />
        
        {/* GarageXpert Connect - Portal Hub Landing Page */}
        <Route path="/connect" element={<ConnectHubPage />} />
        
        {/* AI Garage - Customer Portal */}
        <Route path="/ai-garage" element={<AIGaragePage />} />
        
        {/* Company Portal - B2B Fleet Management */}
        <Route path="/company-portal" element={<CompanyPortalPage />} />
        
        {/* Live Workshop Display - Public (no auth) */}
        <Route path="/live-display/:token" element={<LiveDisplayPublic />} />
        
        {/* Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
      </Routes>
      </Suspense>
      
      {/* Show Footer only for regular website pages */}
      {showWebsiteChrome && <Footer />}
      
      {/* Cookie consent only for regular website pages */}
      {showWebsiteChrome && <CookieConsent />}
      
      {/* Bottom nav ONLY in installed PWA and not on GarageXpert or Portal */}
      {!isGarageXpert && !isPortalRoute && <MobileBottomNav />}
      
      <Toaster />
    </>
  );
};

function App() {
  const [isPWA, setIsPWA] = useState(false);
  
  useEffect(() => {
    // Check if running as installed PWA (standalone mode)
    const checkPWAMode = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone === true  // iOS Safari
        || document.referrer.includes('android-app://'); // Android TWA
      
      setIsPWA(isStandalone);
    };
    
    checkPWAMode();
    
    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handler = (e) => setIsPWA(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return (
    <AuthProvider>
      <PWAProvider>
        <div className="App">
          <BrowserRouter>
            <AppContent isPWA={isPWA} />
          </BrowserRouter>
        </div>
      </PWAProvider>
    </AuthProvider>
  );
}

export default App;
